import React, { useState, useEffect } from 'react';
import { availabilityApi } from '../services/api';
import { Card, Button, Table, Modal, Form, Alert, Row, Col } from 'react-bootstrap';

const AvailabilityPage = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentRule, setCurrentRule] = useState(null);
  const [formData, setFormData] = useState({
    ruleType: 'recurring',
    selectedDays: [],
    date: '',
    startTime: '09:00',
    endTime: '17:00',
    breakStart: '',
    breakEnd: '',
    isAvailable: true,
    notes: ''
  });

  useEffect(() => {
    fetchAvailabilityRules();
  }, []);

  const fetchAvailabilityRules = async () => {
    try {
      setLoading(true);
      const response = await availabilityApi.getAllRules();
      setRules(response.data.data.rules);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load availability rules');
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = (rule = null) => {
    if (rule) {
      setCurrentRule(rule);
      setFormData({
        ruleType: rule.ruleType,
        selectedDays: rule.dayOfWeek ? [rule.dayOfWeek] : [],
        date: rule.date ? new Date(rule.date).toISOString().split('T')[0] : '',
        startTime: rule.startTime || '09:00',
        endTime: rule.endTime || '17:00',
        breakStart: rule.breakStart || '',
        breakEnd: rule.breakEnd || '',
        isAvailable: rule.isAvailable,
        notes: rule.notes || ''
      });
    } else {
      setCurrentRule(null);
      setFormData({
        ruleType: 'recurring',
        selectedDays: [],
        date: '',
        startTime: '09:00',
        endTime: '17:00',
        breakStart: '',
        breakEnd: '',
        isAvailable: true,
        notes: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentRule(null);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDayToggle = (day) => {
    setFormData(prev => {
      const selectedDays = prev.selectedDays || [];
      if (selectedDays.includes(day)) {
        return {
          ...prev,
          selectedDays: selectedDays.filter(d => d !== day)
        };
      } else {
        return {
          ...prev,
          selectedDays: [...selectedDays, day]
        };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate that at least one day is selected for recurring rules
    if (formData.ruleType === 'recurring' && (!formData.selectedDays || formData.selectedDays.length === 0)) {
      setError('Please select at least one day of the week');
      return;
    }

    try {
      if (currentRule) {
        // Update existing rule - only one day since we're editing a single rule
        const ruleData = {
          ...formData,
          dayOfWeek: formData.selectedDays && formData.selectedDays.length > 0 ? formData.selectedDays[0] : null,
          date: formData.ruleType === 'specific_date' ? new Date(formData.date) : undefined
        };
        delete ruleData.selectedDays;
        await availabilityApi.updateRule(currentRule._id, ruleData);
      } else {
        // Create new rules - one for each selected day
        if (formData.ruleType === 'recurring') {
          // Create a rule for each selected day
          const promises = formData.selectedDays.map(day => {
            const ruleData = {
              ruleType: formData.ruleType,
              dayOfWeek: day,
              startTime: formData.startTime,
              endTime: formData.endTime,
              breakStart: formData.breakStart || undefined,
              breakEnd: formData.breakEnd || undefined,
              isAvailable: formData.isAvailable,
              notes: formData.notes || undefined
            };
            return availabilityApi.createRule(ruleData);
          });
          await Promise.all(promises);
        } else {
          // For non-recurring rules, create a single rule
          const ruleData = {
            ...formData,
            date: formData.ruleType === 'specific_date' ? new Date(formData.date) : undefined
          };
          delete ruleData.selectedDays;
          await availabilityApi.createRule(ruleData);
        }
      }

      fetchAvailabilityRules();
      handleCloseModal();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save availability rule');
    }
  };

  const handleDelete = async (ruleId) => {
    if (window.confirm('Are you sure you want to delete this availability rule?')) {
      try {
        await availabilityApi.deleteRule(ruleId);
        fetchAvailabilityRules();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete availability rule');
      }
    }
  };

  if (loading) {
    return <div>Loading availability rules...</div>;
  }

  return (
    <div className="availability-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Availability Management</h2>
        <Button variant="primary" onClick={() => handleShowModal()}>
          Add New Rule
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-4">
        <Card.Body>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Type</th>
                <th>Day/Date</th>
                <th>Time Range</th>
                <th>Break</th>
                <th>Status</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.length > 0 ? (
                rules.map(rule => (
                  <tr key={rule._id}>
                    <td>{rule.ruleType.replace('_', ' ')}</td>
                    <td>
                      {rule.ruleType === 'recurring' ? rule.dayOfWeek : new Date(rule.date).toLocaleDateString()}
                    </td>
                    <td>
                      {rule.startTime} - {rule.endTime}
                    </td>
                    <td>
                      {rule.breakStart && rule.breakEnd ? `${rule.breakStart} - ${rule.breakEnd}` : 'None'}
                    </td>
                    <td>
                      <span className={`badge bg-${rule.isAvailable ? 'success' : 'danger'}`}>
                        {rule.isAvailable ? 'Available' : 'Unavailable'}
                      </span>
                    </td>
                    <td>{rule.notes}</td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleShowModal(rule)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(rule._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center">No availability rules found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{currentRule ? 'Edit Availability Rule' : 'Add New Availability Rule'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Rule Type</Form.Label>
                  <Form.Select
                    name="ruleType"
                    value={formData.ruleType}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="recurring">Recurring (Weekly)</option>
                    <option value="specific_date">Specific Date</option>
                    <option value="break">Break</option>
                    <option value="blackout">Blackout Date</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Availability</Form.Label>
                  <Form.Select
                    name="isAvailable"
                    value={formData.isAvailable}
                    onChange={handleFormChange}
                    required
                  >
                    <option value={true}>Available</option>
                    <option value={false}>Unavailable</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {formData.ruleType === 'recurring' && (
              <Form.Group className="mb-3">
                <Form.Label>Days of Week *</Form.Label>
                <div className="d-flex flex-wrap gap-3">
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                    <Form.Check
                      key={day}
                      type="checkbox"
                      id={`day-${day}`}
                      label={day}
                      checked={formData.selectedDays?.includes(day) || false}
                      onChange={() => handleDayToggle(day)}
                    />
                  ))}
                </div>
                {formData.selectedDays?.length === 0 && (
                  <Form.Text className="text-danger">
                    Please select at least one day
                  </Form.Text>
                )}
              </Form.Group>
            )}

            {formData.ruleType === 'specific_date' && (
              <Form.Group className="mb-3">
                <Form.Label>Date</Form.Label>
                <Form.Control
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleFormChange}
                  required
                />
              </Form.Group>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Time</Form.Label>
                  <Form.Control
                    type="time"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleFormChange}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>End Time</Form.Label>
                  <Form.Control
                    type="time"
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleFormChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Break Start (optional)</Form.Label>
                  <Form.Control
                    type="time"
                    name="breakStart"
                    value={formData.breakStart}
                    onChange={handleFormChange}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Break End (optional)</Form.Label>
                  <Form.Control
                    type="time"
                    name="breakEnd"
                    value={formData.breakEnd}
                    onChange={handleFormChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                name="notes"
                value={formData.notes}
                onChange={handleFormChange}
                rows={3}
              />
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {currentRule ? 'Update Rule' : 'Add Rule'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AvailabilityPage;
